import React, { FC, MouseEvent, FocusEvent, KeyboardEvent } from "react";
import classNames from "classnames";
import { observer } from "mobx-react";
import { handleMouseUpByBlurring } from "../../../utils/focus";

enum CardTheme {
  Dark = "dark",
  Light = "light",
}

interface IBaseCardProps {
  theme?: CardTheme;
  className?: string;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  onMouseEnter?: (event: MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: MouseEvent<HTMLElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  tabIndex?: number;
}

function getCardClassName(theme: CardTheme, className?: string, clickable?: boolean, draggable?: boolean): string {
  return classNames("card", `card--${theme}`, className, {
    "card--clickable": clickable,
    "card--draggable": draggable,
  });
}

/**
 * A basic presentational card
 */
const Card: FC<IBaseCardProps> = observer(({ theme = CardTheme.Light, className, ...props }) => {
  return <div className={getCardClassName(theme, className)} {...props} />;
});

export interface ILinkCardProps extends IBaseCardProps {
  href: string;
  target?: string;
  rel?: string;
}

/**
 * A clickable card using an `<a />` element
 */
const LinkCard: FC<ILinkCardProps> = observer(({ theme = CardTheme.Light, className, target, rel, ...props }) => {
  const defaultRel = target === "_blank" ? "noopener noreferrer" : undefined;

  return (
    <a
      className={getCardClassName(theme, className, true)}
      rel={rel || defaultRel}
      target={target}
      onMouseUp={handleMouseUpByBlurring}
      {...props}
    />
  );
});

export interface IButtonCardProps extends IBaseCardProps {
  onClick: (event: MouseEvent<HTMLElement>) => void;
  disabled?: boolean;

  /**
   * Changes the card's hover styling to appear draggable
   */
  isDraggable?: boolean;
}

/**
 * A clickable card using a `<button />` element
 */
const ButtonCard: FC<IButtonCardProps> = observer(({ theme = CardTheme.Light, className, isDraggable, ...props }) => {
  return (
    <button
      className={getCardClassName(theme, className, true, !!isDraggable)}
      onMouseUp={handleMouseUpByBlurring}
      {...props}
    />
  );
});

export { Card, LinkCard, ButtonCard, CardTheme };
